import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, ensureEntityFolder, getClient, createClient_, updateClient, getProfiles, addActivityLog, fullNameLastFirst, commitAuthChange } from '../lib/supabase';
import CareTeamEditor from '../components/directory/CareTeamEditor';
import { fmtPhone, facilityAddress, listFacilities } from '../lib/directory';
import { voidStaleDraftPackets } from '../lib/documents/packets';
import { useAuth } from '../hooks/useAuth';
import { GA_COUNTIES } from '../lib/gaCounties';
import { countyForZip } from '../lib/zipCounty';
import { loadApprovedCounties, isApprovedCounty as inApprovedArea } from '../lib/serviceArea';

import { PAYERS, SERVICE_LINES, statusesForPayer, isPrivateTrack, isReceivingService } from '../lib/serviceModel';
import StageTransitionModal from '../components/StageTransitionModal';
import PetsQuestion from '../components/PetsQuestion';
import { petColumns } from '../lib/pets';
import ClientTrail from '../components/ClientTrail';

const Field = ({ label, required, hint, children, fieldError }) => (
  <div className="form-group">
    <div className="form-label">{label}{required && <span className="req"> *</span>}</div>
    {fieldError
      ? React.cloneElement(children, { style: { ...(children.props.style || {}), borderColor: 'var(--danger)', background: 'var(--danger-bg)' } })
      : children}
    {fieldError && <div style={{ fontSize:11, color:'var(--danger)', marginTop:3, fontWeight:600 }}>⚠ {fieldError}</div>}
    {!fieldError && hint && <div className="form-hint">{hint}</div>}
  </div>
);

const Section = ({ title, children, overflow }) => (
  <div className="card" style={{ marginBottom: 16, overflow }}>
    <div style={{ padding: '12px 18px', borderBottom: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{title}</div>
    </div>
    <div style={{ padding: '16px 18px' }}>{children}</div>
  </div>
);

const CHECKBOX_FIELDS = {
  abilities: ['Walks Independently','Walks With Assistance','Sit Independently','Sit With Support','Stand Independently','Stand With Support','Non-Ambulatory'],
  medical_equipment: ['Oxygen','Nebulizer','Suction','Pulse Oximeter','Walker','Wheelchair','Bath Chair','Gait Trainer','AFOs','G-Tube','Feeding Pump'],
  therapies: ['Physical Therapy','Occupational Therapy','Speech Therapy','Feeding Therapy','Behavioral Therapy','Vision Therapy'],
  assistance_needed: ['Bathing','Personal care','Feeding','Toileting','Medication','Housekeeping','Mobility Assistance','Positioning','Supervision'],
};

export default function ClientForm() {
  const { id } = useParams();
  const careTeamRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { profile } = useAuth();
  const isNew = !id || id === 'new';
  const originalServiceType = useRef(null);   // to detect a GAPP↔Private switch on save
  // The authorization as it stood when the form loaded. The Prior Auth page
  // versions every change through commitAuthChange; this form used to write
  // the same columns straight to `clients`, so a PA edited here moved the
  // current value forward and left prior_auth_history behind. Comparing
  // against this lets the save log a version too.
  const originalAuth = useRef(null);
  const initialTab = searchParams.get('tab') || 'basic';

  const [activeTab, setActiveTab] = useState(initialTab);
  const [staff, setStaff] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [emergencySameAsGuardian, setEmergencySameAsGuardian] = useState(false);

  // The saved record, for the Change stage popup, which needs fields this form
  // does not hold (AxisCare link, eligibility, DMA-80 notice).
  const [savedClient, setSavedClient] = useState(null);
  const [showStage, setShowStage] = useState(false);
  const [form, setForm] = useState({
    first_name:'', middle_name:'', last_name:'', suffix:'', goes_by:'', date_of_birth:'', gender:'', language:'English',
    address:'', city:'', county:'', state:'GA', zip:'',
    medicaid_id:'', private_insurance:false, insurance_name:'', axiscare_client_id:'',
    age_range:'', service_type:'gapp', service_lines:['home_health'], client_type:['New'], companion_sitter_only:false,
    guardian_first_name:'', guardian_last_name:'', guardian_relationship:'',
    guardian_phone:'', guardian_email:'', guardian_address:'',
    emergency_first_name:'', emergency_last_name:'', emergency_relationship:'',
    emergency_phone:'', emergency_email:'', emergency_address:'',
    diagnosis_summary:'', referral_source:'', referral_date:'', referral_facility_id:'',
    doctor_name:'', doctor_npi:'', doctor_practice:'', doctor_address:'', doctor_phone:'', doctor_fax:'',
    ordering_physician_name:'', ordering_physician_specialty:'', ordering_physician_npi:'', ordering_physician_practice:'', ordering_physician_address:'', ordering_physician_phone:'', ordering_physician_fax:'',
    responsible_person_id:'', responsible_person_name:'',
    current_status:'New Referral', general_notes:'',
    household_pets:[], household_pets_other:'', household_pets_notes:'',
    // Clinical
    full_diagnoses:'', diagnosis_code:'', weight_lbs:'', seizures:false, g_tube:false,
    uses_oxygen:false, potty_trained:false, diapers:false, contractures:false,
    abilities:[], medical_equipment:[], therapies:[], assistance_needed:[],
    diet_description:'', swallowing_issues:false, has_allergies:false, allergies:'',
    current_medications:'', in_school:false, school_hours_per_day:'',
    personal_nurse_in_school:false, has_iep:false,
    in_dfcs_custody:false, dfcs_case_manager:'', gapp_household_member:false,
    // Eligibility
    medicaid_eligible:'Pending', eligibility_checked_date:'', eligibility_checked_by:'',
    eligibility_recheck_date:'', eligibility_notes:'',
    // DMA
    dma_6a_number:'', dma_80_pa_number:'', dma_6a_submission_date:'', dma_6a_expiry_date:'',
    dma_80_submission_date:'', pa_expiry_date:'', pa_renewal_date:'',
    pa_skilled_hours_week:'', pa_pss_hours_week:'',
    nurse_assessment_complete:false,
  });

  useEffect(() => {
    getProfiles().then(({ data }) => setStaff(data || []));
    if (!isNew) {
      getClient(id).then(({ data }) => {
        if (data) {
          const cleaned = {};
          Object.keys(form).forEach(k => {
            if (data[k] !== null && data[k] !== undefined) {
              cleaned[k] = Array.isArray(data[k]) ? data[k] : (typeof data[k] === 'string' && data[k].startsWith('{"') ? [] : data[k]);
            }
          });
          // Migrate legacy specialist_* columns → ordering_physician_* if newer columns are empty
          if (!data.ordering_physician_name && data.specialist_name) cleaned.ordering_physician_name = data.specialist_name;
          if (!data.ordering_physician_npi && data.specialist_npi) cleaned.ordering_physician_npi = data.specialist_npi;
          if (!data.ordering_physician_practice && data.specialist_practice) cleaned.ordering_physician_practice = data.specialist_practice;
          if (!data.ordering_physician_address && data.specialist_address) cleaned.ordering_physician_address = data.specialist_address;
          if (!data.ordering_physician_phone && data.specialist_phone) cleaned.ordering_physician_phone = data.specialist_phone;
          if (!data.ordering_physician_fax && data.specialist_fax) cleaned.ordering_physician_fax = data.specialist_fax;
          // A blank county with a ZIP that settles it is filled in on open,
          // marked as from-ZIP, and only saved if the person saves.
          if (!cleaned.county && cleaned.zip) {
            const z = countyForZip(cleaned.zip);
            if (z.kind === 'fill') { cleaned.county = z.counties[0]; countyAuto.current = true; }
          }
          setForm(prev => ({ ...prev, ...cleaned }));
          setSavedClient(data);
          originalServiceType.current = data.service_type || null;
          originalAuth.current = {
            dma_6a_number:          data.dma_6a_number ?? null,
            dma_6a_submission_date: data.dma_6a_submission_date ?? null,
            dma_6a_expiry_date:     data.dma_6a_expiry_date ?? null,
            dma_80_pa_number:      data.dma_80_pa_number ?? null,
            pa_expiry_date:        data.pa_expiry_date ?? null,
            pa_renewal_date:       data.pa_renewal_date ?? null,
            pa_skilled_hours_week: data.pa_skilled_hours_week ?? null,
            pa_pss_hours_week:     data.pa_pss_hours_week ?? null,
          };
        }
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // While "Same as Parent / Guardian" is checked, keep Emergency Contact mirrored
  // to whatever the guardian fields currently say.
  useEffect(() => {
    if (!emergencySameAsGuardian) return;
    setForm(prev => ({
      ...prev,
      emergency_first_name: prev.guardian_first_name,
      emergency_last_name: prev.guardian_last_name,
      emergency_relationship: prev.guardian_relationship,
      emergency_phone: prev.guardian_phone,
      emergency_email: prev.guardian_email,
      emergency_address: prev.guardian_address,
    }));
  }, [emergencySameAsGuardian, form.guardian_first_name, form.guardian_last_name, form.guardian_relationship, form.guardian_phone, form.guardian_email, form.guardian_address]);

  // The directory is the source of truth for a linked physician; these text
  // columns are the snapshot that packets, CMNs and reports still read.
  const syncCareTeam = (team) => {
    const pcp = team.find(m => m.role === 'pcp')?.physician;
    const ord = team.find(m => m.role === 'ordering')?.physician;
    const nameOf = (p) => `${p.first_name} ${p.last_name}`.trim() + (p.credentials ? `, ${p.credentials}` : '');
    setForm(f => ({
      ...f,
      ...(pcp ? { doctor_name: nameOf(pcp), doctor_npi: pcp.npi || '', doctor_practice: pcp.facility_name || '', doctor_address: facilityAddress(pcp), doctor_phone: pcp.phone ? fmtPhone(pcp.phone) : '', doctor_fax: pcp.fax ? fmtPhone(pcp.fax) : '' } : {}),
      ...(ord ? { ordering_physician_name: nameOf(ord), ordering_physician_specialty: ord.specialty || '', ordering_physician_npi: ord.npi || '', ordering_physician_practice: ord.facility_name || '', ordering_physician_address: facilityAddress(ord), ordering_physician_phone: ord.phone ? fmtPhone(ord.phone) : '', ordering_physician_fax: ord.fax ? fmtPhone(ord.fax) : '' } : {}),
    }));
    setLinked({ pcp: !!pcp, ordering: !!ord });
  };
  const [linked, setLinked] = useState({ pcp: false, ordering: false });
  const [facilities, setFacilities] = useState([]);
  useEffect(() => { listFacilities().then(({ data }) => setFacilities((data || []).filter(f => f.status === 'active'))); }, []);

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm(prev => ({ ...prev, [field]: val }));
  };

  // County from ZIP — a suggestion that never overrides a person's choice.
  // countyAuto marks a county that CAERA filled in, so a later ZIP edit may
  // replace it; a county someone picked by hand is never touched.
  //
  // Whether the county is in the approved area is worked out from Settings →
  // Approved Counties (lib/serviceArea.js), never stored on the client — the
  // hand-set "Approved County" Yes/No it replaces disagreed with the list for
  // 12 of 33 active clients (removed 2026-10-20). This is a staff form; the
  // note never appears on anything a family or referrer sees.
  const countyAuto = React.useRef(false);
  const [approvedList, setApprovedList] = useState(null); // null until loaded
  useEffect(() => { loadApprovedCounties().then(({ data, error }) => setApprovedList(error ? null : data)); }, []);
  // The decision is made here, from the form as rendered, and the state update
  // is a plain merge. Deciding inside the updater mutated countyAuto there, and
  // React runs updaters twice in development to expose exactly that: the
  // second run saw the reset flag and kept the old county.
  const setZip = (e) => {
    const zip = e.target.value;
    const z = countyForZip(zip);
    const mayReplace = !form.county || countyAuto.current;
    const patch = { zip };
    if (mayReplace && z.kind === 'fill') {
      patch.county = z.counties[0];
      countyAuto.current = true;
    } else if (mayReplace && z.kind === 'choose' && !z.counties.includes(form.county)) {
      patch.county = '';                            // a crossing ZIP is never guessed
      countyAuto.current = false;
    }
    setForm(prev => ({ ...prev, ...patch }));
  };
  const setCounty = (e) => {
    const county = e.target.value;
    countyAuto.current = false;
    setForm(prev => ({ ...prev, county }));
    setFieldErrors(fe => { if (!('county' in fe)) return fe; const n = { ...fe }; delete n.county; return n; });
  };

  const toggleArray = (field, val) => {
    setForm(prev => ({
      ...prev,
      [field]: prev[field].includes(val) ? prev[field].filter(v => v !== val) : [...prev[field], val],
    }));
  };

  const handleStaffChange = (e) => {
    const s = staff.find(p => p.id === e.target.value);
    setForm(prev => ({ ...prev, responsible_person_id: e.target.value, responsible_person_name: s?.full_name || '' }));
  };

  const isActive = isReceivingService(form);
  const isGappClient = form.service_type === 'gapp';
  const lines = Array.isArray(form.service_lines) ? form.service_lines : [];
  const suppliesOnly = lines.includes('dme') && !lines.includes('home_health');
  const needsHomeCareAuth = !suppliesOnly;

  // Clearing a field's error must REMOVE the key. Setting it to undefined left
  // it in the object, and the banner renders on Object.keys(...).length — so
  // correcting a field swapped a useful message for a red box listing nothing,
  // which tells someone to fix something without saying what.
  const clearError = (key) => (prev) => {
    if (!(key in prev)) return prev;
    const next = { ...prev }; delete next[key]; return next;
  };

  const handleSave = async () => {
    const errs = {};
    if (!form.first_name)  errs.first_name  = 'First name is required.';
    if (!form.last_name)   errs.last_name   = 'Last name is required.';
    if (!Array.isArray(form.service_lines) || form.service_lines.length === 0)
      errs.service_lines = 'Select at least one service — Home Health, DME / Supplies, or both.';
    if (!form.guardian_first_name)  errs.guardian_first_name  = 'Guardian first name is required.';
    if (!form.guardian_last_name)   errs.guardian_last_name   = 'Guardian last name is required.';
    if (!form.guardian_phone)       errs.guardian_phone       = 'Guardian phone is required.';
    // County decides the approved-area check and the Service Counties map; a
    // blank one drops a child off both without a word.
    if (isActive && !form.county) errs.county = 'County is required for active clients.';
    // Setting this status emails the family that their packet is with DCH, so
    // it cannot be set from here without an address either — the same guard the
    // Move Stage modal applies.
    if (form.current_status === 'Submitted (DMA80)' && !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test((form.guardian_email || '').trim()))
      errs.guardian_email = 'A guardian email is required before the DMA-80 is submitted — the family is emailed when the packet goes to DCH.';
    if (!form.emergency_first_name) errs.emergency_first_name = 'Emergency contact first name is required.';
    if (!form.emergency_last_name)  errs.emergency_last_name  = 'Emergency contact last name is required.';
    if (!form.emergency_phone)      errs.emergency_phone      = 'Emergency contact phone is required.';
    // AxisCare carries visit hours, so it is required of anyone receiving care in
    // the home — but not of a client we only ship supplies to.
    if (isActive && needsHomeCareAuth && !form.axiscare_client_id) errs.axiscare_client_id = 'AxisCare Client ID is required for active clients.';
    // DMA-6A / DMA-80 are GAPP authorization artifacts. Requiring them of a
    // non-GAPP client would force staff to invent authorization numbers.
    if (isActive && isGappClient && !form.dma_6a_number)      errs.dma_6a_number      = 'DMA 6A Number is required for active GAPP clients.';
    if (isActive && isGappClient && !form.dma_6a_submission_date) errs.dma_6a_submission_date = 'DMA 6A Submission Date is required for active GAPP clients.';
    if (isActive && isGappClient && !form.dma_6a_expiry_date)     errs.dma_6a_expiry_date     = 'DMA 6A Expiry Date is required for active GAPP clients.';
    if (isActive && isGappClient && !form.dma_80_pa_number)       errs.dma_80_pa_number       = 'DMA 80 / PA Number is required for active GAPP clients.';
    if (isActive && isGappClient && !form.pa_expiry_date)         errs.pa_expiry_date         = 'PA Expiry Date is required for active GAPP clients.';

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      // Build a readable summary and auto-navigate to the right tab
      const hasBasic = errs.first_name || errs.last_name || errs.axiscare_client_id || errs.service_lines
        || errs.guardian_first_name || errs.guardian_last_name || errs.guardian_phone || errs.guardian_email
        || errs.emergency_first_name || errs.emergency_last_name || errs.emergency_phone || errs.county;
      const hasDma   = errs.dma_6a_number || errs.dma_6a_submission_date || errs.dma_6a_expiry_date || errs.dma_80_pa_number || errs.pa_expiry_date;
      if (hasDma && !hasBasic) setActiveTab('dma');
      else setActiveTab('basic');
      setError('Please complete the required fields highlighted below before saving.');
      return;
    }
    setFieldErrors({});
    setSaving(true); setError('');

    const payload = {
      ...form,
      ...petColumns(form.household_pets, form.household_pets_other, form.household_pets_notes),
      // service_type is set directly from the service type selector
      service_type: form.service_type || 'gapp',
      // client_type holds referral tags only (New / Transfer / Spanish Speaking Only)
      client_type: form.client_type,
      // Numeric fields — convert empty string to null to avoid Postgres integer errors
      weight_lbs:           form.weight_lbs           ? parseFloat(form.weight_lbs)           : null,
      pa_skilled_hours_week: form.pa_skilled_hours_week !== '' && form.pa_skilled_hours_week != null ? parseFloat(form.pa_skilled_hours_week) : null,
      pa_pss_hours_week:     form.pa_pss_hours_week     !== '' && form.pa_pss_hours_week     != null ? parseFloat(form.pa_pss_hours_week)     : null,
      school_hours_per_day: form.school_hours_per_day ? parseFloat(form.school_hours_per_day) : null,
      axiscare_client_id:   form.axiscare_client_id   ? parseInt(form.axiscare_client_id, 10) : null,
      // Date fields — convert empty string to null
      date_of_birth:            form.date_of_birth            || null,
      referral_date:            form.referral_date            || null,
      referral_facility_id:     form.referral_facility_id     || null,
      dma_6a_submission_date:   form.dma_6a_submission_date   || null,
      dma_6a_expiry_date:       form.dma_6a_expiry_date       || null,
      dma_80_submission_date:   form.dma_80_submission_date   || null,
      pa_expiry_date:           form.pa_expiry_date           || null,
      pa_renewal_date:          form.pa_renewal_date          || null,
      eligibility_checked_date: form.eligibility_checked_date || null,
      eligibility_recheck_date: form.eligibility_recheck_date || null,
      // Text fields that must be null not empty string when blank
      responsible_person_id:    form.responsible_person_id    || null,
      insurance_name:           form.insurance_name           || null,
    };

    // Trim every string on the way in. Nothing here was validating whitespace,
    // so a number copied out of GAMMIS arrived with the tab that followed it —
    // G060's PA number was stored 13 characters long. resolvePriorAuth trims on
    // the way out, so no claim ever carried it, but the record was wrong and it
    // showed in the UI. Edges only: a diagnosis paragraph or a pasted
    // eligibility table has internal whitespace for a reason.
    for (const [k, v] of Object.entries(payload)) {
      if (typeof v === 'string') {
        const t = v.replace(/^[\s\u00a0]+|[\s\u00a0]+$/g, '');
        payload[k] = t === '' ? (v === '' ? '' : null) : t;
      }
    }

    if (isNew) {
      // Soft duplicate check — warn, don't block. Common names are legitimate
      // (twins, siblings, unrelated families), so this is a confirmation
      // prompt, not a hard stop.
      const { data: possibleDupes } = await supabase
        .from('clients')
        .select('client_id, first_name, last_name, date_of_birth, current_status')
        .ilike('first_name', form.first_name)
        .ilike('last_name', form.last_name)
        .neq('current_status', 'Duplicate');
      if (possibleDupes?.length) {
        const list = possibleDupes.map(c =>
          `${c.client_id} — ${c.current_status}${c.date_of_birth ? `, DOB ${c.date_of_birth}` : ''}`
        ).join('\n');
        const proceed = window.confirm(
          `A client named "${form.first_name} ${form.last_name}" already exists:\n\n${list}\n\nCreate a new, separate record anyway?`
        );
        if (!proceed) { setSaving(false); return; }
      }

      const { data, error: err } = await createClient_({ ...payload, created_by: profile.id });
      if (err) { setError(err.message); setSaving(false); return; }
      await careTeamRef.current?.commit(data.id);
      await addActivityLog({ client_id: data.id, client_ref: data.client_id, activity_type: 'Client Created', notes: 'Client record manually created by staff.', performed_by_name: profile.full_name, performed_by_id: profile.id, new_status: form.current_status });
      ensureEntityFolder('client', data.client_id).then(folderId => folderId && updateClient(data.id, { drive_folder_id: folderId }));
      navigate(`/clients/${data.id}`);
    } else {
      // Status changes only through Change stage (client_change_status), which
      // logs them and runs what closing a client does. Never save it from here,
      // or a form opened before a stage change would quietly undo it.
      delete payload.current_status;
      delete payload.client_status_zone;
      // Retired 2026-10-20: the approved area is worked out, never stored.
      delete payload.approved_county;
      // A changed authorization is a new version, not an overwrite. The Prior
      // Auth page has always logged one; this form did not, which is why
      // prior_auth_history stopped moving in June while the client records
      // rolled forward. Same helper, so both paths produce the same history.
      //
      // Both kinds are versioned, and they version SEPARATELY. A client carries
      // a DMA-6A and a PA at once, with their own numbers, dates and lifetimes —
      // `commitAuthChange` supersedes by auth_type for exactly that reason, and
      // an earlier version of this block covered only the PA fields, so a 6A
      // edited here changed the record and left the history empty. Whichever
      // set actually changed is the one that gets a new version; editing one
      // must not supersede the other.
      //
      // The changed authorization columns are NOT written by updateClient
      // below: they go through commitAuthChange, which writes the version and
      // the client's columns in one transaction. Saving the client first and
      // versioning after is how the cache and the log came apart — a failed
      // version was only a console warning (fixed 2026-10-19).
      const AUTH_SETS = [
        {
          type: 'PA',
          fields: ['dma_80_pa_number', 'pa_expiry_date', 'pa_renewal_date', 'pa_skilled_hours_week', 'pa_pss_hours_week'],
          record: () => ({
            auth_number:        payload.dma_80_pa_number || null,
            submitted_date:     payload.dma_80_submission_date || null,
            expiry_date:        payload.pa_expiry_date || null,
            renewal_date:       payload.pa_renewal_date || null,
            skilled_hours_week: payload.pa_skilled_hours_week,
            pss_hours_week:     payload.pa_pss_hours_week,
          }),
          expiry: () => payload.pa_expiry_date,
        },
        {
          type: 'DMA 6A',
          fields: ['dma_6a_number', 'dma_6a_submission_date', 'dma_6a_expiry_date'],
          record: () => ({
            auth_number:    payload.dma_6a_number || null,
            submitted_date: payload.dma_6a_submission_date || null,
            expiry_date:    payload.dma_6a_expiry_date || null,
          }),
          expiry: () => payload.dma_6a_expiry_date,
        },
      ];

      const before = originalAuth.current;
      const changedSets = AUTH_SETS.filter(set =>
        before && set.fields.some(k => String(before[k] ?? '') !== String(payload[k] ?? '')));
      const clientPayload = { ...payload };
      for (const set of changedSets) for (const k of set.fields) delete clientPayload[k];

      const { error: err } = await updateClient(id, clientPayload);
      if (err) { setError(err.message); setSaving(false); return; }

      for (const set of changedSets) {
        const { error: authErr } = await commitAuthChange({
          clientId: id,
          clientRef: form.client_id || null,
          authType: set.type,
          record: { ...set.record(), notes: 'Recorded from the client record.' },
          currentColumns: Object.fromEntries(set.fields.map(k => [k, payload[k] ?? null])),
          profile,
        });
        if (authErr) {
          setError(`The rest of the record is saved, but the ${set.type} change was not: ${authErr.message}. The authorization is exactly as it was — try saving again.`);
          setSaving(false);
          return;
        }

        await addActivityLog({ client_id: id, client_ref: form.client_id, activity_type: 'Auth Updated',
          notes: `${set.type} updated from the client record — expires ${set.expiry() || 'not set'}.`,
          performed_by_name: profile.full_name, performed_by_id: profile.id });
        originalAuth.current = { ...originalAuth.current, ...Object.fromEntries(set.fields.map(k => [k, payload[k] ?? null])) };
      }

      // Service track changed (GAPP↔Private): void half-built, unsigned packets of
      // the now-wrong type. Signed/completed packets are preserved as history.
      const newType = payload.service_type;
      if (originalServiceType.current && newType && originalServiceType.current !== newType) {
        try {
          const voided = await voidStaleDraftPackets(id, newType, payload.service_lines);
          await addActivityLog({ client_id: id, client_ref: form.client_id, activity_type: 'Service Type Changed',
            notes: `Service type changed from ${originalServiceType.current} to ${newType}.${voided ? ` ${voided} unsigned draft packet(s) voided.` : ''}`,
            performed_by_name: profile.full_name, performed_by_id: profile.id });
          originalServiceType.current = newType;
        } catch (e) { console.warn('Packet cleanup on service-type change failed:', e); }
      }
      navigate(`/clients/${id}`);
    }
  };

  const tabs = [
    { key: 'basic', label: 'Basic Info' },
    { key: 'clinical', label: 'Clinical / Nurse Assessment' },
    { key: 'dma', label: 'Prior Authorization' },
  ];

  return (
    <div style={{ maxWidth: 800 }}>
      {id
        ? <ClientTrail clientId={id} crumbs={[{ label: 'Edit' }]} />
        : <ClientTrail root={[{ label: 'Clients', to: '/clients' }]} crumbs={[{ label: 'Add client' }]} />}
      {showStage && savedClient && (
        <StageTransitionModal
          client={savedClient}
          onClose={() => setShowStage(false)}
          onSuccess={(newStatus) => {
            setForm(p => ({ ...p, current_status: newStatus }));
            setSavedClient(c => ({ ...c, current_status: newStatus }));
          }}
        />
      )}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)} style={{
            padding: '10px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer',
            color: activeTab === tab.key ? 'var(--accent)' : 'var(--text3)',
            background: 'transparent', border: 'none',
            borderBottom: activeTab === tab.key ? '2px solid var(--accent)' : '2px solid transparent',
            fontFamily: 'inherit', transition: 'all 0.15s',
          }}>{tab.label}</button>
        ))}
      </div>

      {activeTab === 'basic' && (
        <>
          {/* The service is the primary fact about a client — it decides which
              workflows, paperwork and supervision apply. The payer is real but
              secondary, so it sits under the service as a compact row rather
              than leading the form. Both are data-driven from serviceModel so
              adding a line or a payer is a list edit, not a form rewrite. */}
          <Section title="Services">
            <Field label="What are we providing?" required fieldError={fieldErrors.service_lines}>
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', paddingTop:4 }}>
                {SERVICE_LINES.map(opt => {
                  const sl = Array.isArray(form.service_lines) ? form.service_lines : [];
                  const active = sl.includes(opt.key);
                  return (
                    <label key={opt.key} style={{ display:'flex', gap:10, alignItems:'flex-start', padding:'10px 14px', border:`2px solid ${active ? 'var(--accent)' : 'var(--border)'}`, borderRadius:8, cursor:'pointer', background: active ? 'var(--accent-bg)' : '#fff', transition:'all 0.15s', flex:'1 1 auto', minWidth:220 }}>
                      <input type="checkbox" checked={active} onChange={() => {
                        setForm(p => {
                          const cur = Array.isArray(p.service_lines) ? p.service_lines : [];
                          return { ...p, service_lines: cur.includes(opt.key) ? cur.filter(x => x !== opt.key) : [...cur, opt.key] };
                        });
                        setFieldErrors(clearError('service_lines'));
                      }} style={{ width:16, height:16, marginTop:2, flexShrink:0, accentColor:'var(--accent)', cursor:'pointer' }} />
                      <div>
                        <div style={{ fontSize:12, fontWeight:600, color: active ? 'var(--accent)' : 'var(--text)' }}>{opt.label}</div>
                        <div style={{ fontSize:11, color:'var(--text3)' }}>{opt.desc}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </Field>

            <Field label="Payer" required hint="Who authorizes and pays. Separate from the service — a GAPP child who also gets supplies is GAPP here with both services above.">
              <div style={{ display:'flex', gap:8, flexWrap:'wrap', paddingTop:4 }}>
                {PAYERS.map(opt => {
                  const active = form.service_type === opt.key;
                  return (
                    <label key={opt.key} title={opt.desc} style={{ display:'flex', alignItems:'center', gap:7, padding:'6px 14px', border:`1px solid ${active ? 'var(--accent2)' : 'var(--border)'}`, borderRadius:20, cursor:'pointer', fontSize:12, fontWeight:500, color: active ? 'var(--accent2)' : 'var(--text2)', background: active ? 'var(--accent-bg)' : '#fff', transition:'all 0.15s' }}>
                      <input type="radio" name="service_type" value={opt.key} checked={active} onChange={() => setForm(p => ({ ...p, service_type: opt.key }))} style={{ display:'none' }} />
                      {active ? '✓ ' : ''}{opt.label}
                    </label>
                  );
                })}
              </div>
            </Field>

            {isPrivateTrack(form) && (
              <Field label="Supervisory visit cadence"
                hint="Determines the HFRD supervisory visit interval — 92 days for personal care, 122 days for companion/sitter-only tasks (Rule 111-8-65-.10).">
                <label style={{ display:'flex', alignItems:'center', gap:7, padding:'6px 14px', border:`1px solid ${form.companion_sitter_only ? 'var(--accent2)' : 'var(--border)'}`, borderRadius:20, cursor:'pointer', fontSize:12, fontWeight:500, color: form.companion_sitter_only ? 'var(--accent2)' : 'var(--text2)', background: form.companion_sitter_only ? 'var(--accent-bg)' : '#fff', transition:'all 0.15s', width:'fit-content' }}>
                  <input type="checkbox" checked={!!form.companion_sitter_only} onChange={() => setForm(p => ({ ...p, companion_sitter_only: !p.companion_sitter_only }))} style={{ display:'none' }} />
                  {form.companion_sitter_only ? '✓ ' : ''}Companion/Sitter tasks only (122-day cadence)
                </label>
              </Field>
            )}
          </Section>

          <Section title="Client Information">
            <div className="form-row">
              <Field label="First Name" required fieldError={fieldErrors.first_name}><input className="form-input" value={form.first_name} onChange={e => { set('first_name')(e); setFieldErrors(clearError('first_name')); }} /></Field>
              <Field label="Middle Name / Initial"><input className="form-input" value={form.middle_name || ''} onChange={set('middle_name')} placeholder="Optional" /></Field>
              <Field label="Last Name" required fieldError={fieldErrors.last_name}><input className="form-input" value={form.last_name} onChange={e => { set('last_name')(e); setFieldErrors(clearError('last_name')); }} /></Field>
            </div>
            <div className="form-row">
              <Field label="Suffix"><input className="form-input" value={form.suffix || ''} onChange={set('suffix')} placeholder="Jr., Sr., III — optional" /></Field>
              <Field label="Goes By"><input className="form-input" value={form.goes_by || ''} onChange={set('goes_by')} placeholder="Preferred name — optional" /></Field>
            </div>
            <div className="form-row">
              <Field label="Date of Birth"><input type="date" className="form-input" value={form.date_of_birth} onChange={set('date_of_birth')} /></Field>
              <Field label="Gender">
                <select className="form-select" value={form.gender} onChange={set('gender')}>
                  <option value="">Select...</option><option>Male</option><option>Female</option><option>Other</option>
                </select>
              </Field>
            </div>
            <div className="form-row">
              <Field label="Language"><input className="form-input" value={form.language} onChange={set('language')} /></Field>
              <Field label="Client Type">
                <div style={{ display:'flex', gap:8, flexWrap:'wrap', paddingTop:4 }}>
                  {['New','Transfer','Spanish Speaking Only'].map(type => (
                    <label key={type} style={{ display:'flex', alignItems:'center', gap:7, padding:'6px 12px', border:`1px solid ${(Array.isArray(form.client_type) ? form.client_type : [form.client_type]).includes(type) ? 'var(--accent)' : 'var(--border)'}`, borderRadius:20, cursor:'pointer', fontSize:12, fontWeight:500, color:(Array.isArray(form.client_type) ? form.client_type : [form.client_type]).includes(type) ? 'var(--accent)' : 'var(--text2)', background:(Array.isArray(form.client_type) ? form.client_type : [form.client_type]).includes(type) ? 'var(--accent-bg)' : '#fff', transition:'all 0.15s' }}>
                      <input type="checkbox" checked={(Array.isArray(form.client_type) ? form.client_type : [form.client_type]).includes(type)} onChange={() => {
                        const current = Array.isArray(form.client_type) ? form.client_type : [form.client_type];
                        setForm(p => ({ ...p, client_type: current.includes(type) ? current.filter(t => t !== type) : [...current, type] }));
                      }} style={{ display:'none' }} />
                      {type}
                    </label>
                  ))}
                </div>
              </Field>
            </div>

          </Section>

          <Section title="Address">
            <Field label="Street Address"><input className="form-input" value={form.address} onChange={set('address')} /></Field>
            <div className="form-row cols-3">
              <Field label="City"><input className="form-input" value={form.city} onChange={set('city')} /></Field>
              {(() => {
                const z = countyForZip(form.zip);
                const zip5 = String(form.zip || '').replace(/\D/g, '').slice(0, 5);
                // Quiet notes, in order of what matters: a county that the ZIP
                // says is wrong, then one that is outside the approved area,
                // then where a suggestion came from.
                let hint = null;
                if (form.county && z.kind !== 'unknown' && !z.counties.includes(form.county)) {
                  hint = <span style={{ color: 'var(--warn)' }}>ZIP {zip5} is in {z.counties.join(' or ')}, not {form.county} — check this.</span>;
                } else if (!form.county && z.kind === 'choose') {
                  hint = <>ZIP {zip5} covers {z.counties.join(', ')} — choose one.</>;
                } else if (form.county && approvedList && !inApprovedArea(form.county, approvedList)) {
                  hint = <>{countyAuto.current ? `From ZIP ${zip5} · ` : ''}Outside AHFC's approved service area.</>;
                } else if (form.county && countyAuto.current) {
                  hint = <>From ZIP {zip5} — change if needed.</>;
                }
                const fromZip = z.kind !== 'unknown' ? z.counties : [];
                return (
                  <Field label="County" required={isActive} hint={hint} fieldError={fieldErrors.county}>
                    <select className="form-select" value={form.county} onChange={setCounty}>
                      <option value="">— Select County —</option>
                      {fromZip.length > 0 && (
                        <optgroup label={`From ZIP ${zip5}`}>
                          {fromZip.map(c => <option key={`z-${c}`} value={c}>{c}</option>)}
                        </optgroup>
                      )}
                      <optgroup label="All Georgia counties">
                        {GA_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </optgroup>
                    </select>
                  </Field>
                );
              })()}
              <Field label="State"><input className="form-input" value="GA" readOnly style={{ background: 'var(--bg2)', color: 'var(--text3)', cursor: 'not-allowed' }} /></Field>
            </div>
            <div className="form-row">
              <Field label="ZIP Code"><input className="form-input" value={form.zip} onChange={setZip} inputMode="numeric" /></Field>
            </div>
          </Section>

          <Section title="Insurance & Funding">
            <div className="form-row">
              <Field label="Medicaid ID"><input className="form-input" value={form.medicaid_id} onChange={set('medicaid_id')} /></Field>
              <Field label="AxisCare Client ID" required={isActive} hint={isActive ? "Required for active clients — links visit hours in AxisCare" : "Numeric ID from AxisCare — links visit hours to this patient"} fieldError={fieldErrors.axiscare_client_id}><input className="form-input" value={form.axiscare_client_id || ''} onChange={e => { set('axiscare_client_id')(e); setFieldErrors(clearError('axiscare_client_id')); }} placeholder="e.g. 2" /></Field>
              <Field label="Private Insurance">
                <select className="form-select" value={form.private_insurance ? 'Yes' : 'No'} onChange={e => setForm(p => ({ ...p, private_insurance: e.target.value === 'Yes' }))}>
                  <option>No</option><option>Yes</option>
                </select>
              </Field>
            </div>
            {form.private_insurance && (
              <Field label="Insurance Name & Member ID"><input className="form-input" value={form.insurance_name} onChange={set('insurance_name')} /></Field>
            )}
          </Section>

          <Section title="Parent / Guardian">
            <div className="form-row">
              <Field label="First Name" required fieldError={fieldErrors.guardian_first_name}><input className="form-input" value={form.guardian_first_name} onChange={e => { set('guardian_first_name')(e); setFieldErrors(clearError('guardian_first_name')); }} /></Field>
              <Field label="Last Name" required fieldError={fieldErrors.guardian_last_name}><input className="form-input" value={form.guardian_last_name} onChange={e => { set('guardian_last_name')(e); setFieldErrors(clearError('guardian_last_name')); }} /></Field>
            </div>
            <Field label="Relationship"><input className="form-input" value={form.guardian_relationship} onChange={set('guardian_relationship')} placeholder="Mom, Dad, Legal Guardian..." /></Field>
            <div className="form-row">
              <Field label="Phone" required fieldError={fieldErrors.guardian_phone}><input className="form-input" value={form.guardian_phone} onChange={e => { set('guardian_phone')(e); setFieldErrors(clearError('guardian_phone')); }} /></Field>
              <Field label="Email" fieldError={fieldErrors.guardian_email}><input type="email" className="form-input" value={form.guardian_email} onChange={e => { set('guardian_email')(e); setFieldErrors(clearError('guardian_email')); }} /></Field>
            </div>
            <Field label="Address">
              <div style={{ display:'flex', gap:8 }}>
                <input className="form-input" value={form.guardian_address} onChange={set('guardian_address')} placeholder="Street, City, State ZIP" />
                <button type="button" onClick={() => setForm(p => ({ ...p, guardian_address: [p.address, p.city, p.state, p.zip].filter(Boolean).join(', ') }))}
                  style={{ background:'var(--accent-bg)', color:'var(--accent)', border:'1px solid var(--accent-border)', borderRadius:7, padding:'0 12px', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap' }}>
                  ↳ Same as client
                </button>
              </div>
            </Field>
          </Section>

          <Section title="Household">
            <PetsQuestion required={false} hint="Leave blank if the family has not been asked. Shown on the profile and checked when a caregiver is assigned."
              value={{ pets: form.household_pets || [], other: form.household_pets_other || '', notes: form.household_pets_notes || '' }}
              onChange={v => setForm(p => ({ ...p, household_pets: v.pets, household_pets_other: v.other, household_pets_notes: v.notes }))} />
          </Section>

          <Section title="Emergency Contact">
            <label style={{ display:'flex', alignItems:'center', gap:9, padding:'11px 13px', marginBottom:14, background:'var(--copper-bg)', border:'1px solid var(--copper-border)', borderRadius:7, cursor:'pointer' }}>
              <input type="checkbox" checked={emergencySameAsGuardian} onChange={e => setEmergencySameAsGuardian(e.target.checked)} style={{ width:16, height:16, accentColor:'var(--copper)', cursor:'pointer', flexShrink:0 }} />
              <span style={{ fontSize:13, fontWeight:600, color:'var(--text)' }}>
                Same as Parent / Guardian above <span style={{ fontSize:11.5, color:'var(--text3)', fontWeight:400, marginLeft:4 }}>— copies name, relationship, phone, email &amp; address</span>
              </span>
            </label>
            <div className="form-row">
              <Field label="First Name" required fieldError={fieldErrors.emergency_first_name}><input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_first_name} onChange={e => { set('emergency_first_name')(e); setFieldErrors(clearError('emergency_first_name')); }} /></Field>
              <Field label="Last Name" required fieldError={fieldErrors.emergency_last_name}><input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_last_name} onChange={e => { set('emergency_last_name')(e); setFieldErrors(clearError('emergency_last_name')); }} /></Field>
            </div>
            <Field label="Relationship"><input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_relationship} onChange={set('emergency_relationship')} /></Field>
            <div className="form-row">
              <Field label="Phone" required fieldError={fieldErrors.emergency_phone}><input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_phone} onChange={e => { set('emergency_phone')(e); setFieldErrors(clearError('emergency_phone')); }} /></Field>
              <Field label="Email"><input type="email" className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_email} onChange={set('emergency_email')} /></Field>
            </div>
            <Field label="Address">
              <div style={{ display:'flex', gap:8 }}>
                <input className="form-input" disabled={emergencySameAsGuardian} value={form.emergency_address} onChange={set('emergency_address')} placeholder="Street, City, State ZIP" />
                <button type="button" disabled={emergencySameAsGuardian} onClick={() => setForm(p => ({ ...p, emergency_address: p.guardian_address }))}
                  style={{ background:'var(--accent-bg)', color:'var(--accent)', border:'1px solid var(--accent-border)', borderRadius:7, padding:'0 12px', fontSize:12, fontWeight:600, cursor: emergencySameAsGuardian ? 'not-allowed' : 'pointer', whiteSpace:'nowrap', opacity: emergencySameAsGuardian ? 0.5 : 1 }}>
                  ↳ Same as guardian's
                </button>
              </div>
            </Field>
          </Section>

          <Section title="Referral & Assignment">
            <div className="form-row">
              <Field label="Referral Source">
                <select className="form-select" value={form.referral_source} onChange={set('referral_source')}>
                  <option value="">Select...</option>
                  <option>CHOA</option><option>KIDMED</option><option>Doctor referral</option><option>Word of mouth</option>
                  <option>Online search</option><option>Social media</option><option>Other</option>
                </select>
              </Field>
              <Field label="Referral Date"><input type="date" className="form-input" value={form.referral_date} onChange={set('referral_date')} /></Field>
            </div>
            {['CHOA', 'KIDMED', 'Doctor referral', 'Other'].includes(form.referral_source) && (
              <Field label="Referring facility / practice" hint="From the Provider Directory — makes the referral report count by practice">
                <select className="form-select" value={form.referral_facility_id || ''} onChange={set('referral_facility_id')}>
                  <option value="">— not linked —</option>
                  {facilities.map(f => <option key={f.id} value={f.id}>{f.ref_code} · {f.name}{f.city ? ` (${f.city})` : ''}</option>)}
                </select>
              </Field>
            )}
            <div className="form-row">
              {isNew ? (
                <Field label="Current Status">
                  <select className="form-select" value={form.current_status} onChange={set('current_status')}>
                    {statusesForPayer(form.service_type, form.current_status).map(s => <option key={s}>{s}</option>)}
                  </select>
                </Field>
              ) : (
                <Field label="Current Status" hint="Stage changes are logged with a note, and closing a client ends their schedules.">
                  <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                    <div className="form-input" style={{ flex:1, background:'var(--bg2)', color:'var(--text)' }}>{form.current_status}</div>
                    <button type="button" className="btn btn-outline btn-sm" disabled={!savedClient} onClick={() => setShowStage(true)}>Change stage</button>
                  </div>
                </Field>
              )}
              <Field label="Responsible Person">
                <select className="form-select" value={form.responsible_person_id} onChange={handleStaffChange}>
                  <option value="">Unassigned</option>
                  {staff.map(s => <option key={s.id} value={s.id}>{fullNameLastFirst(s.full_name)}</option>)}
                </select>
              </Field>
            </div>
          </Section>
        </>
      )}

      {activeTab === 'clinical' && (
        <>
          <div style={{ background: 'var(--accent-bg)', border: '1px solid var(--accent-border)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--accent2)' }}>
            These fields are completed by the nurse during the in-home assessment visit.
          </div>

          <Section title="Diagnosis & Vitals">
            <Field label="Brief Summary (from intake form)"><textarea className="form-textarea" value={form.diagnosis_summary} onChange={set('diagnosis_summary')} rows={2} /></Field>
            <Field label="Full Medical Diagnoses (nurse assessment)"><textarea className="form-textarea" value={form.full_diagnoses} onChange={set('full_diagnoses')} rows={3} placeholder="Complete list of diagnoses as documented by nurse..." /></Field>
            <Field label="Diagnosis Code(s)" hint="Structured ICD-10 code(s) — this is what feeds billing, keep it separate from the free-text summaries above."><input className="form-input" value={form.diagnosis_code} onChange={set('diagnosis_code')} placeholder="e.g. Q76.49, M62.81" style={{ fontFamily: 'monospace' }} /></Field>
            <div className="form-row">
              <Field label="Weight (lbs)"><input type="number" className="form-input" value={form.weight_lbs} onChange={set('weight_lbs')} /></Field>
              <Field label="Diet Description"><input className="form-input" value={form.diet_description} onChange={set('diet_description')} /></Field>
            </div>
          </Section>

          <Section title="Medical Flags">
            {[['seizures','Seizures'],['g_tube','G-Tube'],['uses_oxygen','Uses Oxygen'],['potty_trained','Potty Trained'],['diapers','Wears Diapers / Pull-Ups'],['contractures','Contractures'],['swallowing_issues','Swallowing Issues / Aspiration Risk'],['has_allergies','Has Allergies']].map(([field, label]) => (
              <div key={field} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: '1px solid var(--border)' }}>
                <input type="checkbox" id={field} checked={!!form[field]} onChange={set(field)} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--accent)' }} />
                <label htmlFor={field} style={{ fontSize: 13, color: 'var(--text)', cursor: 'pointer' }}>{label}</label>
              </div>
            ))}
            {form.has_allergies && (
              <div style={{ marginTop: 12 }}>
                <Field label="List Allergies"><input className="form-input" value={form.allergies} onChange={set('allergies')} /></Field>
              </div>
            )}
            <div style={{ marginTop: 12 }}>
              <Field label="Current Medications"><textarea className="form-textarea" value={form.current_medications} onChange={set('current_medications')} rows={2} /></Field>
            </div>
          </Section>

          {Object.entries(CHECKBOX_FIELDS).map(([field, options]) => (
            <Section key={field} title={field.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {options.map(opt => (
                  <label key={opt} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 12px', border: `1px solid ${form[field]?.includes(opt) ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 500, color: form[field]?.includes(opt) ? 'var(--accent)' : 'var(--text2)', background: form[field]?.includes(opt) ? 'var(--accent-bg)' : '#fff', transition: 'all 0.15s' }}>
                    <input type="checkbox" checked={form[field]?.includes(opt)} onChange={() => toggleArray(field, opt)} style={{ display: 'none' }} />
                    {opt}
                  </label>
                ))}
              </div>
            </Section>
          ))}

          <Section title="School & DFCS">
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
              {[['in_school','Currently in School'],['personal_nurse_in_school','Personal Nurse in School'],['has_iep','Has IEP'],['in_dfcs_custody','In DFCS Custody'],['gapp_household_member','GAPP Household Member']].map(([field, label]) => (
                <label key={field} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={!!form[field]} onChange={set(field)} style={{ width: 16, height: 16, accentColor: 'var(--accent)' }} />
                  {label}
                </label>
              ))}
            </div>
            {form.in_school && (
              <Field label="Hours per day in school"><input type="number" step="0.5" className="form-input" value={form.school_hours_per_day} onChange={set('school_hours_per_day')} style={{ maxWidth: 120 }} /></Field>
            )}
            {form.in_dfcs_custody && (
              <Field label="DFCS Case Manager (Name, Email & Phone)"><input className="form-input" value={form.dfcs_case_manager} onChange={set('dfcs_case_manager')} /></Field>
            )}
          </Section>

          <Section title="Care Team — physicians" overflow="visible">
            <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 10 }}>
              Pick from the Provider Directory and the details fill themselves. One PCP, one ordering physician (the NPI that goes on orders, CMNs and the GAPP packet), any number of specialists.
              Wrong number? Fix it once in <a href="/general-info?tab=physicians" target="_blank" rel="noreferrer" style={{ color: 'var(--accent)' }}>General Info → Physicians</a> and every client gets it.
            </div>
            <CareTeamEditor ref={careTeamRef} clientId={isNew ? null : id} onChange={syncCareTeam} />
          </Section>

          <details style={{ marginBottom: 16 }} open={!linked.pcp && !!form.doctor_name}>
            <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text3)', padding: '4px 0' }}>
              Typed physician details {linked.pcp || linked.ordering ? '(filled from the directory — read-only)' : '(legacy — not linked to the directory yet)'}
            </summary>
            <Section title={`Primary Care Physician${linked.pcp ? ' · from directory' : ''}`}>
              <fieldset disabled={linked.pcp} style={{ border: 'none', padding: 0, margin: 0, opacity: linked.pcp ? 0.7 : 1 }}>
                <div className="form-row">
                  <Field label="Physician Name"><input className="form-input" value={form.doctor_name} onChange={set('doctor_name')} /></Field>
                  <Field label="NPI Number"><input className="form-input" value={form.doctor_npi} onChange={set('doctor_npi')} placeholder="10-digit NPI" /></Field>
                </div>
                <div className="form-row">
                  <Field label="Practice / Clinic Name"><input className="form-input" value={form.doctor_practice} onChange={set('doctor_practice')} /></Field>
                  <Field label="Address"><input className="form-input" value={form.doctor_address} onChange={set('doctor_address')} /></Field>
                </div>
                <div className="form-row">
                  <Field label="Phone"><input className="form-input" value={form.doctor_phone} onChange={set('doctor_phone')} /></Field>
                  <Field label="Fax"><input className="form-input" value={form.doctor_fax} onChange={set('doctor_fax')} /></Field>
                </div>
              </fieldset>
            </Section>

            <Section title={`Ordering Physician${linked.ordering ? ' · from directory' : ''}`}>
              <fieldset disabled={linked.ordering} style={{ border: 'none', padding: 0, margin: 0, opacity: linked.ordering ? 0.7 : 1 }}>
                <div className="form-row">
                  <Field label="Physician Name"><input className="form-input" value={form.ordering_physician_name} onChange={set('ordering_physician_name')} /></Field>
                  <Field label="Specialty"><input className="form-input" value={form.ordering_physician_specialty} onChange={set('ordering_physician_specialty')} /></Field>
                </div>
                <div className="form-row">
                  <Field label="NPI Number"><input className="form-input" value={form.ordering_physician_npi} onChange={set('ordering_physician_npi')} placeholder="10-digit NPI" /></Field>
                  <Field label="Practice / Clinic Name"><input className="form-input" value={form.ordering_physician_practice} onChange={set('ordering_physician_practice')} /></Field>
                </div>
                <div className="form-row">
                  <Field label="Address"><input className="form-input" value={form.ordering_physician_address} onChange={set('ordering_physician_address')} /></Field>
                  <Field label="Phone"><input className="form-input" value={form.ordering_physician_phone} onChange={set('ordering_physician_phone')} /></Field>
                </div>
                <div className="form-row">
                  <Field label="Fax"><input className="form-input" value={form.ordering_physician_fax} onChange={set('ordering_physician_fax')} /></Field>
                </div>
              </fieldset>
            </Section>
          </details>

          <Section title="General Notes">
            <textarea
              className="form-textarea"
              value={form.general_notes || ''}
              onChange={set('general_notes')}
              placeholder="Any additional notes about this client that don't fit elsewhere..."
              rows={4}
            />
          </Section>

          <Section title="Assessment Status">
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, fontWeight: 500 }}>
              <input type="checkbox" checked={!!form.nurse_assessment_complete} onChange={set('nurse_assessment_complete')} style={{ width: 18, height: 18, accentColor: 'var(--success)' }} />
              Mark nurse assessment as complete
            </label>
          </Section>
        </>
      )}

      {activeTab === 'dma' && (
        <>
        <Section title="Medicaid Eligibility Check (GAMMIS)">
          <div style={{ background:'var(--accent-bg)', border:'1px solid var(--accent-border)', borderRadius:8, padding:'10px 14px', marginBottom:14, fontSize:13, color:'var(--accent2)' }}>
            {form.medicaid_eligible === 'Not Applicable' 
              ? 'This client is not Medicaid-funded. Medicaid eligibility verification is not required.'
              : 'Verify eligibility on the GAMMIS portal before proceeding to nurse assessment.'}
          </div>
          <div className="form-row">
            <Field label="Medicaid Eligible">
              <select className="form-select" value={form.medicaid_eligible || 'Pending'} onChange={set('medicaid_eligible')} style={{ borderColor: form.medicaid_eligible === 'Not Applicable' ? 'var(--border2)' : undefined, background: form.medicaid_eligible === 'Not Applicable' ? 'var(--bg2)' : undefined }}>
                <option value="Pending">Pending Check</option>
                <option value="Yes">Yes — Eligible</option>
                <option value="No">No — Not Eligible</option>
                <option value="Not Applicable" style={{ background: 'var(--bg2)', color: 'var(--text3)' }}>Not Applicable (Non-Medicaid Client)</option>
              </select>
            </Field>
            {form.medicaid_eligible !== 'Not Applicable' && (
              <Field label="Date Checked"><input type="date" className="form-input" value={form.eligibility_checked_date || ''} onChange={set('eligibility_checked_date')} /></Field>
            )}
          </div>
          {form.medicaid_eligible !== 'Not Applicable' && (
            <>
              <div className="form-row">
                <Field label="Checked By">
                  <select className="form-select" value={form.eligibility_checked_by || ''} onChange={set('eligibility_checked_by')}>
                    <option value="">— Select staff member —</option>
                    {staff.map(s => <option key={s.id} value={s.full_name}>{fullNameLastFirst(s.full_name)}</option>)}
                  </select>
                </Field>
                <Field label="Re-check Date"><input type="date" className="form-input" value={form.eligibility_recheck_date || ''} onChange={set('eligibility_recheck_date')} /></Field>
              </div>
              <Field label="Notes from GAMMIS"><textarea className="form-textarea" value={form.eligibility_notes || ''} onChange={set('eligibility_notes')} rows={2} placeholder="Any relevant notes from the eligibility check..." /></Field>
            </>
          )}
        </Section>
        <Section title="Prior Authorization">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1px 1fr', gap:0 }}>
            {/* DMA 6A */}
            <div style={{ paddingRight:20 }}>
              <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--accent2)', marginBottom:14, paddingBottom:6, borderBottom:'1px solid var(--accent-border)' }}>DMA 6A</div>
              <Field label="DMA 6A Number" required={isActive} fieldError={fieldErrors.dma_6a_number}><input className="form-input" value={form.dma_6a_number} onChange={e => { set('dma_6a_number')(e); setFieldErrors(clearError('dma_6a_number')); }} placeholder="DMA 6A reference number" /></Field>
              <Field label="Submission Date" required={isActive} fieldError={fieldErrors.dma_6a_submission_date}><input type="date" className="form-input" value={form.dma_6a_submission_date} onChange={e => { set('dma_6a_submission_date')(e); setFieldErrors(clearError('dma_6a_submission_date')); }} /></Field>
              <Field label="Expiry Date" required={isActive} fieldError={fieldErrors.dma_6a_expiry_date}><input type="date" className="form-input" value={form.dma_6a_expiry_date} onChange={e => { set('dma_6a_expiry_date')(e); setFieldErrors(clearError('dma_6a_expiry_date')); }} /></Field>
            </div>
            {/* Divider */}
            <div style={{ background:'var(--accent-border)', margin:'0 0' }} />
            {/* DMA 80 / PA */}
            <div style={{ paddingLeft:20 }}>
              <div style={{ fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--accent2)', marginBottom:14, paddingBottom:6, borderBottom:'1px solid var(--accent-border)' }}>DMA 80 / PA</div>
              <Field label="DMA 80 / PA Number" required={isActive} fieldError={fieldErrors.dma_80_pa_number}><input className="form-input" value={form.dma_80_pa_number} onChange={e => { set('dma_80_pa_number')(e); setFieldErrors(clearError('dma_80_pa_number')); }} placeholder="DMA 80 or PA number" /></Field>
              <Field label="DMA 80 Submission Date"><input type="date" className="form-input" value={form.dma_80_submission_date} onChange={set('dma_80_submission_date')} /></Field>
              <Field label="PA Expiry Date" required={isActive} fieldError={fieldErrors.pa_expiry_date}><input type="date" className="form-input" value={form.pa_expiry_date} onChange={e => { set('pa_expiry_date')(e); setFieldErrors(clearError('pa_expiry_date')); }} /></Field>
              <Field label="PA Renewal Date"><input type="date" className="form-input" value={form.pa_renewal_date} onChange={set('pa_renewal_date')} /></Field>
              <Field label="Skilled Hrs/Wk"><input type="number" step="0.5" min="0" className="form-input" value={form.pa_skilled_hours_week} onChange={set('pa_skilled_hours_week')} placeholder="e.g. 40" /></Field>
              <Field label="PSS Hrs/Wk"><input type="number" step="0.5" min="0" className="form-input" value={form.pa_pss_hours_week} onChange={set('pa_pss_hours_week')} placeholder="e.g. 25" /></Field>
            </div>
          </div>
        </Section>
        </>
      )}

      {(error || Object.keys(fieldErrors).length > 0) && (
        <div style={{ background:'var(--danger-bg)', border:'1.5px solid var(--danger)', borderRadius:10, padding:'14px 18px', marginBottom:16 }}>
          <div style={{ fontWeight:700, color:'var(--danger)', fontSize:13, marginBottom: Object.keys(fieldErrors).length > 0 ? 8 : 0 }}>
            ⚠ {error || 'Please fix the following before saving:'}
          </div>
          {Object.keys(fieldErrors).length > 0 && (
            <ul style={{ margin:0, paddingLeft:18, fontSize:12, color:'var(--danger)' }}>
              {Object.entries(fieldErrors).map(([, msg]) => msg && <li key={msg}>{msg}</li>)}
            </ul>
          )}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, paddingBottom: 32 }}>
        <button className="btn btn-outline" onClick={() => navigate(isNew ? '/clients' : `/clients/${id}`)}>Cancel</button>
        <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
          {saving ? <span className="loading-spinner" style={{ borderTopColor: '#fff' }} /> : null}
          {isNew ? 'Create Client' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
}
